import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './auth-context';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './auth-context';

const Success = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [status, setStatus] = useState('processing'); // processing | success | error
    const cancelledRef = useRef(false);

    useEffect(() => {
        if (!user) return;

        cancelledRef.current = false;

        const pollForAccess = async () => {
            const maxAttempts = 15; // 15 * 2s = 30s max

            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                if (cancelledRef.current) return;

                try {
                    await user.getIdToken(true);
                    const idTokenResult = await user.getIdTokenResult();

                    const userRef = doc(db, 'users', user.uid);
                    const docSnap = await getDoc(userRef);

                    const hasClaim = idTokenResult.claims.isPaid === true;
                    const hasFirestore = docSnap.exists() && docSnap.data().isPaidUser === true;

                    if (hasClaim || hasFirestore) {
                        if (!cancelledRef.current) {
                            setStatus('success');
                        }
                        return;
                    }
                } catch (err) {
                    console.error('Error checking access:', err);
                }

                await new Promise(resolve => setTimeout(resolve, 2000));
            }

            if (!cancelledRef.current) {
                setStatus('error');
            }
        };

        pollForAccess();

        return () => { cancelledRef.current = true; };
    }, [user]);

    // Redirect after success
    useEffect(() => {
        if (status !== 'success') return;
        const timer = setTimeout(() => navigate('/practice'), 3000);
        return () => clearTimeout(timer);
    }, [status, navigate]);

    return (
        <div className="min-h-screen bg-[#070B14] flex items-center justify-center p-4">
            {/* Ambient glow */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl" />
            </div>

            <div className="relative z-10 rounded-2xl bg-white/5 ring-1 ring-white/10 max-w-md w-full p-8 text-center">
                {status === 'processing' && (
                    <>
                        <div className="h-12 w-12 mx-auto mb-5">
                            <div className="animate-spin h-12 w-12 border-3 border-white/10 border-t-cyan-500 rounded-full" />
                        </div>
                        <h2 className="text-xl font-semibold text-white mb-2">Activating your account...</h2>
                        <p className="text-sm text-white/55">This usually takes just a few seconds.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="h-14 w-14 mx-auto mb-5 rounded-full bg-green-500/10 flex items-center justify-center">
                            <svg className="h-7 w-7 text-green-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-white mb-2">You're all set!</h2>
                        <p className="text-sm text-white/55 mb-6">Full access is now unlocked. Here's what you can do:</p>

                        <div className="text-left rounded-xl bg-white/5 ring-1 ring-white/10 p-4 mb-6 space-y-2">
                            {[
                                'Unlimited practice questions',
                                'All 6 practice modes',
                                'Exam simulations (90 min)',
                                'Spaced repetition reviews',
                                'Domain-specific drills',
                            ].map((feature) => (
                                <div key={feature} className="flex items-center text-sm">
                                    <svg className="h-4 w-4 text-cyan-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-white/70">{feature}</span>
                                </div>
                            ))}
                        </div>

                        <p className="text-xs text-white/45 mb-4">Redirecting to practice...</p>
                        <Link
                            to="/practice"
                            className="inline-flex items-center px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-semibold transition-all"
                        >
                            Start Practicing
                        </Link>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="h-14 w-14 mx-auto mb-5 rounded-full bg-orange-500/10 flex items-center justify-center">
                            <svg className="h-7 w-7 text-orange-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" />
                                <path strokeLinecap="round" d="M12 8v4M12 16h.01" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-white mb-2">Taking longer than expected</h2>
                        <p className="text-sm text-white/55 mb-6">
                            Your payment was received, but it's taking a moment to activate your account. This can happen during high traffic.
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                            >
                                Try Again
                            </button>
                            <Link
                                to="/"
                                className="block w-full py-2.5 rounded-xl ring-1 ring-white/10 text-white/70 hover:bg-white/10 text-sm text-center transition-colors"
                            >
                                Return Home
                            </Link>
                        </div>
                        <p className="text-xs text-white/45 mt-4">
                            If this persists, your access will be activated automatically within a few minutes.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default Success;
